import { useCallback } from 'react'
import { useSessionStore } from '@/stores'
import type { Permission } from './permissions'

/**
 * What the signed-in person may do, for deciding which components to render.
 *
 * FAILS CLOSED, AND THAT IS THE WHOLE CONTRACT. `permissions` is null until the
 * session has been established, and every helper here returns false while it is
 * null. Rendering the permitted state during that window would show a viewer
 * the billing screen and the admin panel for as long as the request takes --
 * briefly, visibly, and on every reload.
 *
 * THIS IS COSMETIC. It exists so people are not shown doors they cannot open,
 * never to decide whether the door opens. Every route re-checks
 * (`current.require(...)`), and which meetings a person may READ is decided by
 * row-level security in PostgreSQL. If this hook and the API ever disagree, the
 * API is right and this is a rendering bug.
 *
 * STALENESS IS ACCEPTED, DELIBERATELY. An administrator who edits a role does
 * not change what an already-loaded browser shows until it reloads or hits a
 * 403. The API re-reads permissions on every request, so the ENFORCEMENT is
 * never stale -- only the menu is. Polling for role changes would put a request
 * per client per interval against the authorization service to keep a sidebar
 * tidy.
 */
export function usePermissions() {
  const permissions = useSessionStore((s) => s.permissions)

  const has = useCallback(
    (permission: Permission) => permissions?.includes(permission) ?? false,
    [permissions],
  )
  const hasAll = useCallback(
    (...required: Permission[]) =>
      permissions ? required.every((p) => permissions.includes(p)) : false,
    [permissions],
  )
  const hasAny = useCallback(
    (...required: Permission[]) =>
      permissions ? required.some((p) => permissions.includes(p)) : false,
    [permissions],
  )

  return {
    /** Null while unknown. Prefer the helpers, which treat null as denied. */
    permissions,
    /** True once the set is known, so a caller can show a skeleton instead. */
    loaded: permissions !== null,
    has,
    hasAll,
    hasAny,
  }
}

/** Single-permission convenience, for the common `{can && <Button/>}` case. */
export function usePermission(permission: Permission): boolean {
  return usePermissions().has(permission)
}
